/**
 * STAT-03/STAT-04 — estado «assinado / sem assinatura digital» de qualquer lista de documentos
 * clínicos, buscado em lote pelos ids exibidos (sem o teto de 100 de `listMine`) e compartilhado
 * entre as telas abertas: `markSigned` atualiza lista, prévia, Hoje e ficha do paciente juntas.
 *
 * Regras (design.md §E): falha no lote mantém o último mapa; id ainda não carregado não aparece
 * no mapa (a UI não mostra chip — nunca um «assinado» ou «sem assinatura» falso).
 */
import { useEffect, useMemo } from 'react'
import { create } from 'zustand'
import {
  signedDocumentsRepository,
  type SignedDocumentListItem,
} from '@/shared/repositories/signedDocuments/signedDocumentsRepository'

/** `null` = consultado e sem assinatura; ausente = ainda não consultado. */
export type SignatureStatusMap = Map<string, SignedDocumentListItem | null>

interface SignatureStatusState {
  byId: Record<string, SignedDocumentListItem | null>
  /** Quando cada id foi consultado (ms). */
  fetchedAt: Record<string, number>
  /** Ids consultados antes deste instante são revalidados no próximo `ensure`. */
  staleAt: number
  inFlight: Record<string, true>
  /** Assinados nesta sessão (`markSigned`): uma consulta atrasada não os volta para «sem assinatura». */
  signedLocally: Record<string, true>
  lastError: string | null
  /** Busca os ids ainda não consultados (ou vencidos por `invalidate`). */
  ensure: (ids: string[]) => Promise<void>
  /** Marca tudo como vencido sem apagar o que está na tela (ex.: voltar à lista, puxar para atualizar). */
  invalidate: () => void
  /** STAT-04: documento assinado agora, em qualquer tela. */
  markSigned: (documentId: string, item?: SignedDocumentListItem | null) => void
  reset: () => void
}

const pickLatest = (
  current: SignedDocumentListItem | undefined,
  next: SignedDocumentListItem,
): SignedDocumentListItem =>
  !current || next.signatureTimestamp > current.signatureTimestamp ? next : current

export const useSignatureStatusStore = create<SignatureStatusState>((set, get) => ({
  byId: {},
  fetchedAt: {},
  staleAt: 0,
  inFlight: {},
  signedLocally: {},
  lastError: null,

  ensure: async (ids) => {
    const { byId, fetchedAt, staleAt, inFlight } = get()
    const wanted = [...new Set(ids.filter(Boolean))].filter(
      (id) => !inFlight[id] && (!(id in byId) || (fetchedAt[id] ?? 0) < staleAt),
    )
    if (!wanted.length) return
    set((state) => ({
      inFlight: { ...state.inFlight, ...Object.fromEntries(wanted.map((id) => [id, true as const])) },
    }))
    const releaseInFlight = (state: SignatureStatusState) => {
      const nextInFlight = { ...state.inFlight }
      for (const id of wanted) delete nextInFlight[id]
      return nextInFlight
    }
    try {
      const rows = await signedDocumentsRepository.listForClinicalDocuments(wanted)
      const latest = new Map<string, SignedDocumentListItem>()
      for (const row of rows) {
        if (row.sourceId) latest.set(row.sourceId, pickLatest(latest.get(row.sourceId), row))
      }
      const now = Date.now()
      set((state) => {
        const nextById = { ...state.byId }
        const nextFetchedAt = { ...state.fetchedAt }
        for (const id of wanted) {
          nextById[id] = latest.get(id) ?? (state.signedLocally[id] ? state.byId[id] : null)
          nextFetchedAt[id] = now
        }
        return { byId: nextById, fetchedAt: nextFetchedAt, inFlight: releaseInFlight(state), lastError: null }
      })
    } catch (error) {
      set((state) => ({
        inFlight: releaseInFlight(state),
        lastError: error instanceof Error ? error.message : String(error),
      }))
    }
  },

  invalidate: () => set({ staleAt: Date.now() + 1 }),

  markSigned: (documentId, item) => {
    const id = documentId.trim()
    if (!id) return
    const signed: SignedDocumentListItem = item ?? {
      id: `local-${id}`,
      documentType: '',
      sourceTable: 'clinical_documents',
      sourceId: id,
      signatureTimestamp: new Date().toISOString(),
      verificationUrl: null,
      signedPdfStoragePath: null,
      patientId: null,
    }
    set((state) => ({
      byId: { ...state.byId, [id]: signed },
      fetchedAt: { ...state.fetchedAt, [id]: Date.now() },
      signedLocally: { ...state.signedLocally, [id]: true },
    }))
  },

  reset: () =>
    set({ byId: {}, fetchedAt: {}, staleAt: 0, inFlight: {}, signedLocally: {}, lastError: null }),
}))

/** Mapa reativo do estado de assinatura dos `ids` (busca em lote os que faltam). */
export function useSignatureStatus(ids: string[]): SignatureStatusMap {
  const key = ids.join('|')
  const byId = useSignatureStatusStore((state) => state.byId)
  const staleAt = useSignatureStatusStore((state) => state.staleAt)

  useEffect(() => {
    if (!key) return
    void useSignatureStatusStore.getState().ensure(key.split('|'))
  }, [key, staleAt])

  return useMemo(() => {
    const map: SignatureStatusMap = new Map()
    if (!key) return map
    for (const id of key.split('|')) {
      if (id in byId) map.set(id, byId[id])
    }
    return map
  }, [byId, key])
}
